
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Settings, Star, Eye, EyeOff, Type, List, Link2 } from 'lucide-react';
import type { CustomAction } from '@/types/action';

interface ActionParametersTabProps {
  action: CustomAction;
}

export const ActionParametersTab = ({ action }: ActionParametersTabProps) => {
  const parameters = action.parameters || [];
  const requiredParams = parameters.filter(p => p.required);
  const optionalParams = parameters.filter(p => !p.required);

  return (
    <div className="space-y-6">
      {/* Parameters Overview */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Settings className="h-5 w-5 text-blue-600" />
            Parameters Overview
            <Badge variant="outline" className="ml-2 text-xs">
              {parameters.length} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {parameters.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="text-2xl font-bold text-red-700">{requiredParams.length}</div>
                <div className="text-sm text-red-600">Required</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">{optionalParams.length}</div>
                <div className="text-sm text-blue-600">Optional</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-gray-700">{parameters.length}</div>
                <div className="text-sm text-gray-600">Total</div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No parameters configured</h3>
              <p className="text-gray-600">This action doesn't require any input parameters.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Required Parameters */}
      {requiredParams.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Star className="h-5 w-5 text-red-600" />
              Required Parameters
              <Badge variant="destructive" className="ml-2 text-xs">
                {requiredParams.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {requiredParams.map((param) => (
                <div key={param.id} className="border rounded-lg p-6 bg-red-50/30 border-red-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center">
                        <Type className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          {param.name}
                          <Star className="h-4 w-4 text-red-500" fill="currentColor" />
                        </h4>
                        {param.description && (
                          <p className="text-sm text-gray-600 mt-1">{param.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="bg-white">{param.type}</Badge>
                      <Badge variant="destructive" className="text-xs">Required</Badge>
                    </div>
                  </div>

                  {/* Parameter Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {param.default !== undefined && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">
                          Default Value
                        </label>
                        <div className="p-3 bg-white rounded border text-sm font-mono">
                          {typeof param.default === 'object' 
                            ? JSON.stringify(param.default, null, 2)
                            : String(param.default)
                          }
                        </div>
                      </div>
                    )}

                    {param.options && param.options.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">
                          Available Options
                        </label>
                        <div className="flex flex-wrap gap-1">
                          {param.options.map((option, index) => (
                            <Badge key={index} variant="secondary" className="text-xs bg-white">
                              {option}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {param.dependencies && param.dependencies.length > 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-800">Dependencies</span>
                      </div>
                      {param.dependencies.map((dep, index) => (
                        <div key={index} className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded mt-1">
                          Depends on: <code className="font-mono">{dep.paramId}</code> {dep.condition.operator} <code className="font-mono">{dep.condition.value}</code>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Optional Parameters */}
      {optionalParams.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <List className="h-5 w-5 text-blue-600" />
              Optional Parameters
              <Badge variant="outline" className="ml-2 text-xs">
                {optionalParams.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {optionalParams.map((param) => (
                <div key={param.id} className="border rounded-lg p-6 bg-blue-50/30 border-blue-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Type className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          {param.name}
                          {param.visible === false ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </h4>
                        {param.description && (
                          <p className="text-sm text-gray-600 mt-1">{param.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="bg-white">{param.type}</Badge>
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">Optional</Badge>
                    </div>
                  </div>

                  {/* Parameter Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {param.default !== undefined && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">
                          Default Value
                        </label>
                        <div className="p-3 bg-white rounded border text-sm font-mono">
                          {typeof param.default === 'object' 
                            ? JSON.stringify(param.default, null, 2)
                            : String(param.default)
                          }
                        </div>
                      </div>
                    )}

                    {param.options && param.options.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1 block">
                          Available Options
                        </label>
                        <div className="flex flex-wrap gap-1">
                          {param.options.map((option, index) => (
                            <Badge key={index} variant="secondary" className="text-xs bg-white">
                              {option}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {param.dependencies && param.dependencies.length > 0 && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <Link2 className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-800">Dependencies</span>
                      </div>
                      {param.dependencies.map((dep, index) => (
                        <div key={index} className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded mt-1">
                          Depends on: <code className="font-mono">{dep.paramId}</code> {dep.condition.operator} <code className="font-mono">{dep.condition.value}</code>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
